import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Menu, MenuTrigger, MenuPopover, MenuList } from '@fluentui/react-components';
import { PatchTask } from '../../redux/taskSlice';
import styles from './css/index.module.css';

/**
 * TimelineRangePickerMenu
 * Edits Task.start_date and Task.end_date (both optional).
 */
const toDateInputValue = value => {
    if (!value) return '';
    return String(value).split('T')[0];
};

const TimelineRangePickerMenu = ({ task, onTimelineSelect, children, onRequestTimelineChange, schedulingConfirmRequired = false }) => {
    const dispatch = useDispatch();
    const [open, setOpen] = useState(false);
    const [startDate, setStartDate] = useState(toDateInputValue(task?.start_date));
    const [endDate, setEndDate] = useState(toDateInputValue(task?.end_date));

    useEffect(() => {
        setStartDate(toDateInputValue(task?.start_date));
        setEndDate(toDateInputValue(task?.end_date));
    }, [task?.id, task?.start_date, task?.end_date]);

    const isInvalidRange = Boolean(startDate && endDate && startDate > endDate);

    const saveTimeline = async payload => {
        if (!task?.id) return;

        if (schedulingConfirmRequired && onRequestTimelineChange) {
            onRequestTimelineChange(task, payload);
            onTimelineSelect && onTimelineSelect(payload);
            setOpen(false);
            return;
        }

        try {
            await dispatch(
                PatchTask({
                    body: payload,
                    id: task.id,
                    suppressToast: true,
                }),
            );
            onTimelineSelect && onTimelineSelect(payload);
            setOpen(false);
        } catch (error) {
            console.error('Error updating task timeline:', error);
        }
    };

    const handleSaveTimeline = async () => {
        if (isInvalidRange) return;
        await saveTimeline({
            start_date: startDate || null,
            end_date: endDate || null,
        });
    };

    const handleClearTimeline = async () => {
        setStartDate('');
        setEndDate('');
        await saveTimeline({
            start_date: null,
            end_date: null,
        });
    };

    return (
        <Menu open={open} onOpenChange={(e, data) => setOpen(data.open)}>
            <MenuTrigger>{children}</MenuTrigger>
            <MenuPopover style={{ width: '320px', minWidth: '320px' }}>
                <MenuList>
                    <div className="p-2">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <span className={styles.tableFilterHeader}>Set Timeline</span>
                        </div>

                        <div className="mb-3">
                            <label className="form-label small mb-0" style={{ fontSize: '10px' }}>
                                Start date
                            </label>
                            <input
                                type="date"
                                className={'form-control form-control-sm ' + styles.filterSelect}
                                value={startDate || ''}
                                onChange={e => setStartDate(e.target.value)}
                            />
                        </div>

                        <div className="mb-2">
                            <label className="form-label small mb-0" style={{ fontSize: '10px' }}>
                                End date
                            </label>
                            <input
                                type="date"
                                className={'form-control form-control-sm ' + styles.filterSelect}
                                value={endDate || ''}
                                onChange={e => setEndDate(e.target.value)}
                            />
                        </div>

                        {isInvalidRange && (
                            <div style={{ fontSize: '11px', color: '#c50f1f', marginBottom: 8 }}>
                                Start date must be on/before end date.
                            </div>
                        )}

                        <div className="d-flex gap-2 justify-content-between">
                            <span
                                className={styles.secondaryBtnText + ' ms-1'}
                                onClick={handleSaveTimeline}
                                style={{
                                    opacity: isInvalidRange ? 0.5 : 1,
                                    cursor: isInvalidRange ? 'not-allowed' : 'pointer',
                                }}>
                                Save timeline
                            </span>
                            <span className={styles.secondaryBtnText + ' ms-1'} onClick={handleClearTimeline}>
                                Clear
                            </span>
                        </div>
                    </div>
                </MenuList>
            </MenuPopover>
        </Menu>
    );
};

export default TimelineRangePickerMenu;
